import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { adminAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Shield, ShieldCheck, UserPlus, Trash2, Eye, EyeOff, Power, History, Users, AlertTriangle, Clock, Edit } from 'lucide-react';
import { toast } from 'sonner';

const AdminManagement = () => {
    const { user } = useAuth();
    const [admins, setAdmins] = useState([]);
    const [loading, setLoading] = useState(true);
    const [permissions, setPermissions] = useState({});
    const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
    const [activeTab, setActiveTab] = useState('admins');
    const [activityLogs, setActivityLogs] = useState([]);
    const [logsTotal, setLogsTotal] = useState(0);
    const [logsLoading, setLogsLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [newAdmin, setNewAdmin] = useState({ email: '', password: '', company_name: 'PRIXDEAL', contact_name: '' });

    const fetchAdmins = useCallback(async () => {
        try {
            setLoading(true);
            const [adminsRes, permRes] = await Promise.all([
                adminAPI.getAdmins(),
                adminAPI.getPermissions()
            ]);
            setAdmins(adminsRes.data.admins || []);
            setPermissions(permRes.data || {});
        } catch (err) {
            toast.error("Erreur lors du chargement des administrateurs");
        } finally {
            setLoading(false);
        }
    }, []);

    const fetchActivityLog = useCallback(async () => {
        try {
            setLogsLoading(true);
            const res = await adminAPI.getActivityLog({ limit: 100 });
            setActivityLogs(res.data.logs || []);
            setLogsTotal(res.data.total || 0);
        } catch {
            toast.error("Erreur lors du chargement du journal d'activite");
        } finally {
            setLogsLoading(false);
        }
    }, []);

    useEffect(() => { fetchAdmins(); }, [fetchAdmins]);
    useEffect(() => { if (activeTab === 'activity' && permissions.is_super_admin) fetchActivityLog(); }, [activeTab, permissions.is_super_admin, fetchActivityLog]);

    const handleCreateAdmin = async (e) => {
        e.preventDefault();
        if (!newAdmin.email || !newAdmin.password || !newAdmin.contact_name) {
            toast.error("Veuillez remplir tous les champs obligatoires");
            return;
        }
        try {
            await adminAPI.createAdmin(newAdmin);
            toast.success("Sous-administrateur cree avec succes");
            setIsCreateDialogOpen(false);
            setNewAdmin({ email: '', password: '', company_name: 'PRIXDEAL', contact_name: '' });
            fetchAdmins();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Erreur lors de la creation");
        }
    };

    const handleToggleAdmin = async (adminId) => {
        try {
            const res = await adminAPI.toggleAdmin(adminId);
            toast.success(res.data.message);
            fetchAdmins();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Erreur");
        }
    };

    const handleDeleteAdmin = async (adminId, adminName) => {
        if (!window.confirm(`Supprimer l'administrateur "${adminName}" ? Cette action est irreversible.`)) return;
        try {
            await adminAPI.deleteAdmin(adminId);
            toast.success("Administrateur supprime");
            fetchAdmins();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Erreur lors de la suppression");
        }
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        return new Date(dateStr).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    };

    const actionLabels = {
        create_offer: "Offre creee",
        create_admin: "Admin cree",
        delete_admin: "Admin supprime",
        toggle_admin: "Admin active/desactive",
        edit_admin_profile: "Profil admin modifie",
        update_order_status: "Statut commande modifie",
        create_order: "Commande creee",
    };

    if (loading) return (
        <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-jungle"></div>
        </div>
    );

    return (
        <div className="space-y-6" data-testid="admin-management-page">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="font-heading text-3xl font-bold tracking-tight text-jungle" data-testid="admin-management-title">
                        Administrateurs
                    </h1>
                    <p className="text-muted-foreground mt-1">
                        {permissions.is_super_admin ? "Gerez vos sous-administrateurs et consultez leur activite" : "Vue de l'equipe d'administration"}
                    </p>
                </div>
                {permissions.is_super_admin && (
                    <Button onClick={() => setIsCreateDialogOpen(true)} className="bg-jungle hover:bg-jungle/90" data-testid="create-admin-button">
                        <UserPlus className="w-4 h-4 mr-2" /> Ajouter un sous-admin
                    </Button>
                )}
            </div>

            {/* Tabs */}
            {permissions.is_super_admin && (
                <div className="flex gap-2 border-b">
                    <button
                        onClick={() => setActiveTab('admins')}
                        className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${activeTab === 'admins' ? 'border-jungle text-jungle' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
                        data-testid="tab-admins"
                    >
                        <Users className="w-4 h-4 inline mr-1" /> Equipe ({admins.length})
                    </button>
                    <button
                        onClick={() => setActiveTab('activity')}
                        className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${activeTab === 'activity' ? 'border-jungle text-jungle' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
                        data-testid="tab-activity"
                    >
                        <History className="w-4 h-4 inline mr-1" /> Journal d'activite ({logsTotal})
                    </button>
                </div>
            )}

            {/* Admin List Tab */}
            {activeTab === 'admins' && (
                <div className="grid gap-4">
                    {admins.map((admin) => (
                        <Card key={admin.id} className={`border ${admin.is_super_admin ? 'border-amber-300 bg-amber-50/50' : admin.is_active === false ? 'border-red-200 bg-red-50/30 opacity-60' : 'border-jungle/10'}`} data-testid={`admin-card-${admin.id}`}>
                            <CardContent className="p-4">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-4">
                                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${admin.is_super_admin ? 'bg-amber-100 text-amber-700' : 'bg-jungle/10 text-jungle'}`}>
                                            {admin.is_super_admin ? <ShieldCheck className="w-5 h-5" /> : <Shield className="w-5 h-5" />}
                                        </div>
                                        <div>
                                            <div className="flex items-center gap-2">
                                                <h3 className="font-semibold text-base">{admin.contact_name || admin.company_name}</h3>
                                                {admin.is_super_admin && (
                                                    <span className="text-xs bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full font-medium">Super Admin</span>
                                                )}
                                                {!admin.is_super_admin && (
                                                    <span className="text-xs bg-jungle/10 text-jungle px-2 py-0.5 rounded-full font-medium">Sous-Admin</span>
                                                )}
                                                {admin.is_active === false && (
                                                    <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full font-medium">Desactive</span>
                                                )}
                                            </div>
                                            <p className="text-sm text-muted-foreground">{admin.email}</p>
                                            <p className="text-xs text-muted-foreground mt-0.5">Cree le {formatDate(admin.created_at)}</p>
                                        </div>
                                    </div>
                                    {permissions.is_super_admin && !admin.is_super_admin && (
                                        <div className="flex items-center gap-2">
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                onClick={() => handleToggleAdmin(admin.id)}
                                                className={admin.is_active !== false ? "text-amber-600 border-amber-300 hover:bg-amber-50" : "text-green-600 border-green-300 hover:bg-green-50"}
                                                data-testid={`toggle-admin-${admin.id}`}
                                            >
                                                <Power className="w-4 h-4 mr-1" />
                                                {admin.is_active !== false ? 'Desactiver' : 'Activer'}
                                            </Button>
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                onClick={() => handleDeleteAdmin(admin.id, admin.contact_name || admin.email)}
                                                className="text-red-600 border-red-300 hover:bg-red-50"
                                                data-testid={`delete-admin-${admin.id}`}
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </Button>
                                        </div>
                                    )}
                                </div>
                            </CardContent>
                        </Card>
                    ))}
                    {admins.length === 0 && (
                        <p className="text-center text-muted-foreground py-12">Aucun administrateur trouve</p>
                    )}
                </div>
            )}

            {/* Activity Log Tab */}
            {activeTab === 'activity' && permissions.is_super_admin && (
                <Card className="border-jungle/10">
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2 text-lg">
                            <History className="w-5 h-5 text-jungle" /> Journal d'activite des sous-admins
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        {logsLoading ? (
                            <div className="flex justify-center py-8">
                                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-jungle"></div>
                            </div>
                        ) : activityLogs.length === 0 ? (
                            <p className="text-center text-muted-foreground py-12">Aucune activite enregistree pour le moment</p>
                        ) : (
                            <div className="space-y-3">
                                {activityLogs.map((log) => (
                                    <div key={log.id} className="flex items-start gap-3 p-3 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors" data-testid={`activity-log-${log.id}`}>
                                        <div className="w-8 h-8 rounded-full bg-jungle/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                                            <Clock className="w-4 h-4 text-jungle" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <span className="font-medium text-sm">{log.admin_name}</span>
                                                <span className="text-xs bg-jungle/10 text-jungle px-2 py-0.5 rounded-full">
                                                    {actionLabels[log.action] || log.action}
                                                </span>
                                            </div>
                                            {log.target && <p className="text-sm text-muted-foreground mt-0.5">Cible: {log.target}</p>}
                                            {log.details && <p className="text-xs text-muted-foreground">{log.details}</p>}
                                            <p className="text-xs text-muted-foreground/60 mt-1">{formatDate(log.created_at)}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </CardContent>
                </Card>
            )}

            {/* Not super admin info */}
            {!permissions.is_super_admin && (
                <Card className="border-amber-200 bg-amber-50/50">
                    <CardContent className="p-4 flex items-center gap-3">
                        <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0" />
                        <div>
                            <p className="text-sm font-medium text-amber-800">Acces limite</p>
                            <p className="text-xs text-amber-600">Vous etes sous-administrateur. Seul le super-admin peut gerer les comptes et voir le journal d'activite. Vous pouvez modifier votre propre profil depuis "Mon Profil".</p>
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Create Admin Dialog */}
            <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <UserPlus className="w-5 h-5 text-jungle" /> Ajouter un sous-administrateur
                        </DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleCreateAdmin} className="space-y-4">
                        <div className="space-y-2">
                            <Label>Nom complet *</Label>
                            <Input
                                value={newAdmin.contact_name}
                                onChange={(e) => setNewAdmin({ ...newAdmin, contact_name: e.target.value })}
                                placeholder="Prenom Nom"
                                required
                                data-testid="new-admin-name"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Email *</Label>
                            <Input
                                type="email"
                                value={newAdmin.email}
                                onChange={(e) => setNewAdmin({ ...newAdmin, email: e.target.value })}
                                placeholder="email@exemple.fr"
                                required
                                data-testid="new-admin-email"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Mot de passe *</Label>
                            <div className="relative">
                                <Input
                                    type={showPassword ? "text" : "password"}
                                    value={newAdmin.password}
                                    onChange={(e) => setNewAdmin({ ...newAdmin, password: e.target.value })}
                                    placeholder="Mot de passe"
                                    required
                                    data-testid="new-admin-password"
                                />
                                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                </button>
                            </div>
                        </div>
                        <div className="space-y-2">
                            <Label>Entreprise</Label>
                            <Input
                                value={newAdmin.company_name}
                                onChange={(e) => setNewAdmin({ ...newAdmin, company_name: e.target.value })}
                                data-testid="new-admin-company"
                            />
                        </div>

                        <div className="bg-muted/30 rounded-lg p-3 text-sm text-muted-foreground">
                            <p className="font-medium text-foreground mb-1">Permissions du sous-admin :</p>
                            <ul className="space-y-1 text-xs">
                                <li>- Gerer les commandes, produits, offres, clients</li>
                                <li>- Modifier son propre profil et mot de passe</li>
                                <li className="text-red-500">- Ne peut PAS ajouter/supprimer d'admin</li>
                                <li className="text-red-500">- Ne peut PAS voir les profils des autres admins</li>
                            </ul>
                        </div>

                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
                                Annuler
                            </Button>
                            <Button type="submit" className="bg-jungle hover:bg-jungle/90" data-testid="submit-create-admin">
                                Creer le sous-admin
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default AdminManagement;
